import { userText, useUserLanguage } from "../../i18n/userManagementPresentation";
/**
 * ACL Permissions Registry (Browser-safe version)
 *
 * This is a browser-compatible subset of the server permissions registry.
 * Keep in sync with: src/domain/acl/permissions.ts
 */

// ─────────────────────────────────────────────────────────────────────────────
// Administrative Permissions
// ─────────────────────────────────────────────────────────────────────────────

export const ADMIN_PERMISSIONS = {
  get "admin.users.read"() { return userText("viewUserListAndDetails"); },
  get "admin.users.create"() { return userText("createNewUsers"); },
  get "admin.users.update"() { return userText("modifyUserAccounts"); },
  get "admin.users.delete"() { return userText("removeUserAccounts"); },
  get "admin.users.impersonate"() { return userText("impersonateOtherUsers"); },
  get "admin.roles.read"() { return userText("viewRoles"); },
  get "admin.roles.manage"() { return userText("createEditDeleteRoles"); },
  get "admin.groups.read"() { return userText("viewGroups"); },
  get "admin.groups.manage"() { return userText("createEditDeleteGroups"); },
  get "admin.settings.read"() { return userText("viewSystemSettings"); },
  get "admin.settings.update"() { return userText("modifySystemSettings"); },
  get "admin.branding.update"() { return userText("modifyInstanceBrandingLogosFaviconDefaultAvatarBanner"); },
  get "admin.sessions.read"() { return userText("viewAllUserSessions"); },
  get "admin.sessions.revoke"() { return userText("revokeUserSessions"); },
  get "admin.system.dashboard"() { return userText("accessSystemDashboard"); },
  get "admin.system.logs"() { return userText("viewSystemLogs"); },
  get "admin.system.console"() { return userText("accessWebConsole"); },
  get "admin.system.database"() { return userText("manageDatabase"); },
  get "admin.system.ssl"() { return userText("manageSSLCertificates"); },
  get "admin.system.vpn"() { return userText("manageVPNSettings"); },
  get "admin.system.update"() { return userText("performSystemUpdates"); },
  get "admin.system.api"() { return userText("manageAPISettings"); },
  get "admin.system.support"() { return userText("enableOrDisableSupportTeamAccess"); },
  get "admin.links.read"() { return userText("viewExternalLinks"); },
  get "admin.links.manage"() { return userText("manageExternalLinks"); },
  get "admin.software.install"() { return userText("installSoftware"); },
  get "admin.software.remove"() { return userText("removeSoftware"); },
  get "admin.software.configure"() { return userText("configureSoftware"); },
  get "admin.apps.config.read"() { return userText("viewAppConfigurationFiles"); },
  get "admin.apps.config.edit"() { return userText("editAppConfigurationFiles"); },
  get "admin.apps.backup.create"() { return userText("createAppBackupsConfigNginxFull"); },
  get "admin.apps.backup.restore"() { return userText("restoreAppBackups"); },
  get "admin.apps.backup.delete"() { return userText("deleteAppBackups"); },
  get "admin.apps.database.browse"() { return userText("browseAppSQLiteDatabaseFiles"); },
  get "admin.streaming.read"() { return userText("viewStreamingDashboard"); },
  get "admin.streaming.manage"() { return userText("manageStreamingSettings"); },
  get "admin.requests.read"() { return userText("viewMediaRequests"); },
  get "admin.requests.manage"() { return userText("approveOrDenyMediaRequests"); },
} as const;

export type AdminPermission = keyof typeof ADMIN_PERMISSIONS;

// ─────────────────────────────────────────────────────────────────────────────
// Admin-scoped feature permissions (browser mirror of the server registry).
// These are admin-only capabilities the sysop can revoke per-admin — NOT
// group-grantable to non-admins. Keep in sync with the server ADMIN_FEATURE_PERMISSIONS.
// ─────────────────────────────────────────────────────────────────────────────

export const ADMIN_FEATURE_PERMISSIONS = {
  get "admin.system.deleteProtection"() { return userText("manageLargeFileDeleteProtectionViewStatusEnableDisableServer"); },
} as const;

export type AdminFeaturePermission = keyof typeof ADMIN_FEATURE_PERMISSIONS;

/** Feature-permission key gating the Delete Protection admin surface (admin-scoped). */
export const DELETE_PROTECTION_PERMISSION: AdminFeaturePermission =
  "admin.system.deleteProtection";

// ─────────────────────────────────────────────────────────────────────────────
// Application Categories
// ─────────────────────────────────────────────────────────────────────────────

export const APP_CATEGORIES = {
  "media-management": {
    get label() { return userText("mediaManagement"); },
    get description() { return userText("appsForOrganizingAndManagingMediaLibraries"); },
    apps: [
      "bazarr",
      "lazylibrarian",
      "lidarr",
      "listenarr",
      "medusa",
      "mylar3",
      "radarr",
      "readarr",
      "sickchill",
      "sickgear",
      "sonarr",
      "sportarr",
    ],
  },
  "media-servers": {
    get label() { return userText("mediaServers"); },
    get description() { return userText("appsForStreamingAndServingMediaContent"); },
    apps: [
      "airsonic",
      "audiobookshelf",
      "dispatcharr",
      "emby",
      "iptvboss",
      "jellyfin",
      "plex",
      "subsonic",
      "tautulli",
      "wsdashboard",
      "xteve",
    ],
  },
  "media-requests": {
    get label() { return userText("mediaRequests"); },
    get description() { return userText("appsForManagingMediaRequests"); },
    apps: ["jellyseerr", "ombi", "overseerr", "requestrr", "seerr"],
  },
  "download-clients": {
    get label() { return userText("downloadClients"); },
    get description() { return userText("torrentAndNZBDownloadClients"); },
    apps: [
      "deluge",
      "flood",
      "jdownloader",
      "nzbget",
      "pyload",
      "qbittorrent",
      "rtorrent",
      "rutorrent",
      "sabnzbd",
      "transmission",
    ],
  },
  indexers: {
    get label() { return userText("indexersTrackers"); },
    get description() { return userText("appsForFindingContentSources"); },
    apps: ["autobrr", "autodl", "jackett", "nzbhydra2", "prowlarr"],
  },
  automation: {
    get label() { return userText("automationTools"); },
    get description() { return userText("appsForAutomatingMediaWorkflows"); },
    apps: [
      "autoscan",
      "filebot",
      "flaresolverr",
      "flexget",
      "headphones",
      "notifiarr",
      "seedcross",
      "unmanic",
      "unpackerr",
    ],
  },
  "file-management": {
    get label() { return userText("fileManagement"); },
    get description() { return userText("appsForManagingAndSyncingFiles"); },
    apps: [
      "btsync",
      "duplicati",
      "filebrowser",
      "nextcloud",
      "rclone",
      "syncthing",
    ],
  },
  readers: {
    get label() { return userText("eBooksComics"); },
    get description() { return userText("appsForReadingDigitalContent"); },
    apps: ["calibre", "kavita", "komga"],
  },
  "remote-access": {
    get label() { return userText("remoteAccess"); },
    get description() { return userText("appsForRemoteSystemAccess"); },
    apps: ["novnc", "webconsole", "x2go"],
  },
  communication: {
    get label() { return userText("communication"); },
    get description() { return userText("iRCAndChatApplications"); },
    apps: ["quassel", "thelounge", "znc"],
  },
  utilities: {
    get label() { return userText("utilitiesSystem"); },
    get description() { return userText("systemUtilitiesAndSecurityTools"); },
    apps: ["fail2ban", "lecert", "netdata", "phpmyadmin", "vpn", "wireguard"],
  },
} as const;

export type AppCategory = keyof typeof APP_CATEGORIES;
export type AppId = (typeof APP_CATEGORIES)[AppCategory]["apps"][number];

// ─────────────────────────────────────────────────────────────────────────────
// Default Roles
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_ROLES = {
  SUPER_ADMIN: {
    id: "super_admin",
    name: "Super Admin",
    description: "Full system access, cannot be removed",
    isSystem: true,
    priority: 100,
  },
  ADMIN: {
    id: "admin",
    name: "Administrator",
    description: "Full administrative access",
    isSystem: true,
    priority: 90,
  },
  STAFF: {
    id: "staff",
    name: "Staff",
    description: "Limited administrative access",
    isSystem: false,
    priority: 50,
  },
  USER: {
    id: "user",
    name: "User",
    description: "Standard user access",
    isSystem: true,
    priority: 10,
  },
  BANNED: {
    id: "banned",
    name: "Banned",
    description: "Banned user - restricted access",
    isSystem: true,
    priority: 0,
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Default Groups
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_GROUPS = {
  ADMINISTRATOR: {
    id: "administrator",
    name: "Administrator",
    description: "Full access to all applications",
    isSystem: true,
    priority: 100,
  },
  ACCESS: {
    id: "access",
    name: "Access",
    description: "Default group with basic download client access",
    isSystem: true,
    priority: 10,
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get all apps for a category
 */
export const getCategoryApps = (categoryId: AppCategory): string[] => {
  return [...(APP_CATEGORIES[categoryId]?.apps ?? [])];
};

/**
 * Get permission domains for UI grouping
 */
export const getPermissionDomains = (): Array<{
  id: string;
  label: string;
  permissions: string[];
}> => {
  const domains: Record<string, string[]> = {};

  for (const key of Object.keys(ADMIN_PERMISSIONS)) {
    const parts = key.split(".");
    const domain = parts.length >= 2 ? `${parts[0]}.${parts[1]}` : parts[0];
    if (!domains[domain]) domains[domain] = [];
    domains[domain].push(key);
  }

  return Object.entries(domains).map(([id, permissions]) => ({
    id,
    label: formatDomainLabel(id),
    permissions,
  }));
};

const formatDomainLabel = (domain: string): string => {
  const parts = domain.split(".");
  if (parts.length < 2) return domain;
  const subject = parts[1];
  return subject.charAt(0).toUpperCase() + subject.slice(1);
};

/**
 * Get app categories as a list for UI
 */
export const getAppCategoryList = (): Array<{
  id: AppCategory;
  label: string;
  description: string;
  apps: string[];
}> => {
  return Object.entries(APP_CATEGORIES).map(([id, cat]) => ({
    id: id as AppCategory,
    label: cat.label,
    description: cat.description,
    apps: [...cat.apps],
  }));
};
